import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Armchair, Grid } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { LEISURECRAFT_PLUNGES, LEISURECRAFT_FURNITURE } from '../data/products';
import { LEISURECRAFT_OUTDOOR_SHOWERS, LEISURECRAFT_OUTDOOR_FURNITURE } from '../data/outdoorCatalogs';
import CallForPricingGrid from '../components/CallForPricingGrid';
import ProductGrid from '../components/ProductGrid';

const LeisureCraftPage = () => {
  const [activeCategory, setActiveCategory] = useState('furniture');
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
      if (++tries >= 8) clearInterval(timer);
    }, 400);
    return () => clearInterval(timer);
  }, [location.hash]);

  const getFilteredProducts = () => {
    switch (activeCategory) {
      case 'plunges':
        return LEISURECRAFT_PLUNGES;
      case 'furniture':
      default:
        return LEISURECRAFT_FURNITURE;
    }
  };

  const categories = [
    { id: 'furniture', label: 'Outdoor Furniture', icon: Armchair, count: LEISURECRAFT_FURNITURE.length },
    { id: 'plunges', label: 'Cold Plunges', icon: Droplets, count: LEISURECRAFT_PLUNGES.length },
  ];

  const filteredProducts = getFilteredProducts();

  return (
    <>
      <Helmet>
        <title>LeisureCraft | Canadian-Made Cold Plunges, Outdoor Furniture & Showers</title>
        <meta name="description" content="Explore LeisureCraft — Canadian-made cedar cold plunge tubs, outdoor furniture and outdoor showers handcrafted in Ontario. Built to last through every season." />
        <meta name="keywords" content="LeisureCraft, Dundalk, Canadian Timber, Pure Cube, cedar sauna, barrel sauna, cold plunge, outdoor furniture, Adirondack, tiki bar" />
        <meta property="og:title" content="LeisureCraft Cold Plunges, Outdoor Furniture & Showers" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="pt-36 pb-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)' }} data-testid="leisurecraft-page">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">Canadian-Made Wellness · Handcrafted in Ontario</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold uppercase text-[#0E3D95] mb-4">
              LeisureCraft
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl">
              Premium cedar cold plunges, outdoor furniture, and outdoor showers built to perform in all seasons.
              Crafted from high-quality Western and Eastern white cedar across the Dundalk, Pure Cube, and
              Canadian Timber collections — designed to become a lasting part of your wellness lifestyle.
            </p>
          </motion.div>

          {/* Category Filter Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-2 md:gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 font-semibold text-sm md:text-base transition-all rounded-lg ${
                    activeCategory === cat.id
                      ? 'bg-[#0E3D95] text-white shadow-lg'
                      : 'bg-white/80 text-[#0E3D95] hover:bg-[#F88200] hover:text-white border border-slate-200'
                  }`}
                  data-testid={`lc-category-btn-${cat.id}`}
                >
                  <cat.icon size={18} />
                  <span>{cat.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeCategory === cat.id ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Results Count */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <p className="text-slate-600">
              Showing <span className="font-semibold text-[#0E3D95]">{filteredProducts.length}</span>{' '}
              <span className="font-semibold text-[#F88200]">{categories.find(c => c.id === activeCategory)?.label}</span>
            </p>
          </motion.div>

          {/* Product Grid */}
          <ProductGrid products={filteredProducts} linkPrefix="/products" />

          {/* Outdoor Showers */}
          <div id="showers" className="scroll-mt-28">
            <CallForPricingGrid
              testId="leisurecraft-outdoor-showers"
              eyebrow="LeisureCraft · 100% Canadian Cedar"
              title="Outdoor Showers"
              subtitle="Rinse off sand, salt or chlorine after the sauna, pool or beach. Premium cedar showers built to weather every season."
              items={LEISURECRAFT_OUTDOOR_SHOWERS}
            />
          </div>

          {/* Outdoor Furniture */}
          <div id="furniture" className="scroll-mt-28">
            <CallForPricingGrid
              testId="leisurecraft-outdoor-furniture"
              eyebrow="LeisureCraft · Canadian Cedar"
              title="Outdoor Furniture"
              subtitle="Tiki bars, Adirondack log chairs & benches, dining tables, bar stools and the modern Pacific line — all built with sauna-grade cedar craftsmanship. Click any piece for photos & details."
              items={LEISURECRAFT_OUTDOOR_FURNITURE}
            />
          </div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-white/80 backdrop-blur-sm p-8 rounded-lg border border-slate-200"
          >
            <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-[#0E3D95] mb-4">
              Build Your Own Backyard Wellness Space
            </h2>
            <div className="grid md:grid-cols-3 gap-6 text-slate-600">
              <div>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Outdoor Showers</h3>
                <p>Canadian cedar pillar, enclosed and barrel outdoor showers — rinse off after the pool, beach or spa. Premium hardware available separately.</p>
              </div>
              <div>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Cold Plunges</h3>
                <p>Cedar cold plunge tubs with stainless steel or HDPE interiors and chiller-compatible temperature control for year-round contrast therapy.</p>
              </div>
              <div>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Outdoor Furniture</h3>
                <p>Adirondack log chairs, benches, dining tables, tiki bars, and the modern Pacific line — all built with sauna-grade cedar craftsmanship.</p>
              </div>
            </div>
            <p className="mt-6 text-slate-500 text-sm">Contact us for pricing and availability on any LeisureCraft product.</p>
          </motion.div>

        </div>
      </div>
    </>
  );
};

export default LeisureCraftPage;
